// src/lib/secondaryLineFormatting.ts
import { formatNumber, formatNumberWithPrecision, LocaleCode } from './locale';
import { formatNumberWithConfig } from './numberFormatting';
import type { NumberFormatConfig } from '../types/redux';

/**
 * Determines the number of decimal places needed for secondary line values
 */
export function determineSecondaryDecimalPlaces(
  values: number[],
  scaleFactor: number
): number {
  const secondaryValues = values.map(v => v * scaleFactor);
  
  let maxDecimals = 0;
  for (const value of secondaryValues) {
    const str = value.toString();
    const decimalIndex = str.indexOf('.');
    if (decimalIndex !== -1) {
      const decimals = str.length - decimalIndex - 1;
      maxDecimals = Math.max(maxDecimals, decimals);
    }
  }
  
  // Cap at 4 decimal places for display
  return Math.min(maxDecimals, 4);
}

/**
 * Formats a fraction for display in math notation
 */
export function formatFractionForMath(
  fraction: { n: number; d: number },
  fractionDisplay: { format: 'mixed' | 'improper'; simplify: boolean }
): string {
  let { n, d } = fraction;
  
  // Simplify if requested
  if (fractionDisplay.simplify) {
    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
    const divisor = gcd(Math.abs(n), d);
    n = n / divisor;
    d = d / divisor;
  }
  
  // Handle whole numbers
  if (d === 1) {
    return n.toString();
  }
  
  // Handle mixed fractions
  if (fractionDisplay.format === 'mixed' && Math.abs(n) >= d) {
    const whole = Math.floor(Math.abs(n) / d);
    const remainder = Math.abs(n) % d;
    const sign = n < 0 ? '-' : '';
    
    if (remainder === 0) {
      return `${sign}${whole}`;
    }
    
    return `${sign}${whole}\\frac{${remainder}}{${d}}`;
  }
  
  // Improper fraction
  if (n < 0) {
    return `-\\frac{${Math.abs(n)}}{${d}}`;
  }
  
  return `\\frac{${n}}{${d}}`;
}

/**
 * Converts a decimal to a fraction representation (simplified)
 */
export function decimalToFraction(decimal: number): { n: number; d: number } {
  // Handle NaN and infinities defensively
  if (!isFinite(decimal) || isNaN(decimal)) {
    return { n: 0, d: 1 };
  }

  // Close to an integer
  const rounded = Math.round(decimal);
  if (Math.abs(decimal - rounded) < 1e-12) {
    return { n: rounded, d: 1 };
  }

  const sign = decimal < 0 ? -1 : 1;
  let value = Math.abs(decimal);

  // Continued fraction expansion with denominator cap
  const maxDenominator = 10000;
  const epsilon = 1e-12;

  let hPrev = 1, kPrev = 0; // previous convergent
  let a0 = Math.floor(value);
  let h = a0, k = 1; // current convergent

  let fracPart = value - a0;
  let a = 0;

  // If there is no fractional part, we already returned above, but double-check
  if (fracPart < epsilon) {
    return { n: sign * h, d: k };
  }

  // Iterate continued fraction terms
  while (k < maxDenominator) {
    if (fracPart < epsilon) break;
    a = Math.floor(1 / fracPart);

    // next convergent
    const hNext = a * h + hPrev;
    const kNext = a * k + kPrev;

    const approximation = hNext / kNext;
    if (Math.abs(approximation - value) < 1e-12) {
      h = hNext;
      k = kNext;
      break;
    }

    // advance
    hPrev = h;
    kPrev = k;
    h = hNext;
    k = kNext;

    const inv = 1 / fracPart - a;
    if (!isFinite(inv) || isNaN(inv)) break;
    fracPart = inv;
  }

  // Reduce fraction to simplest terms
  const gcd = (aNum: number, bNum: number): number => (bNum === 0 ? Math.abs(aNum) : gcd(bNum, aNum % bNum));
  const divisor = gcd(h, k);
  const n = sign * (h / divisor);
  const d = k / divisor;

  return { n, d };
}

/**
 * Converts a decimal to a fraction WITHOUT simplifying.
 * Prefers "round" denominators like 10, 100, etc. for decimal-like values.
 */
export function decimalToUnsimplifiedFraction(decimal: number): { n: number; d: number } {
  // Handle NaN and infinities defensively
  if (!isFinite(decimal) || isNaN(decimal)) {
    return { n: 0, d: 1 };
  }

  // Close to an integer
  const rounded = Math.round(decimal);
  if (Math.abs(decimal - rounded) < 1e-10) {
    return { n: rounded, d: 1 };
  }

  const sign = decimal < 0 ? -1 : 1;
  const absValue = Math.abs(decimal);

  // Try powers of 10 first (these are "decimal-friendly" denominators)
  const powersOf10 = [10, 100, 1000];
  for (const d of powersOf10) {
    const n = absValue * d;
    if (Math.abs(n - Math.round(n)) < 1e-10) {
      return { n: sign * Math.round(n), d };
    }
  }

  // Try common fraction denominators
  const commonDenominators = [2, 3, 4, 5, 6, 8, 12, 15, 16, 20, 24, 25, 30, 50];
  for (const d of commonDenominators) {
    const n = absValue * d;
    if (Math.abs(n - Math.round(n)) < 1e-10) {
      return { n: sign * Math.round(n), d };
    }
  }

  // Fallback: find any denominator up to 1000 that works
  for (let d = 1; d <= 1000; d++) {
    const n = absValue * d;
    if (Math.abs(n - Math.round(n)) < 1e-10) {
      return { n: sign * Math.round(n), d };
    }
  }

  // Last resort: approximate with denominator 100
  return { n: sign * Math.round(absValue * 100), d: 100 };
}

/**
 * Format a secondary line value - PURE FUNCTION with no component dependencies
 * DEPRECATED: Use formatSecondaryValueWithConfig for Phase 3
 */
export function formatSecondaryValue(
  primaryValue: number,
  scaleFactor: number,
  numberType: 'integer' | 'decimal' | 'fraction' | 'percent',
  fractionDisplay: { format: 'mixed' | 'improper'; simplify: boolean },
  locale: LocaleCode,
  decimalPlaces?: number // Optional, will be calculated if not provided
): string {
  const secondaryValue = primaryValue * scaleFactor;
  
  switch (numberType) {
    case 'integer':
      return formatNumber(Math.round(secondaryValue), locale);
      
    case 'decimal': {
      const places = decimalPlaces ?? 2; // Default to 2 if not provided
      return formatNumberWithPrecision(secondaryValue, locale, places);
    }
      
    case 'fraction': {
      // Use unsimplified fraction if simplify is false
      const fraction = fractionDisplay.simplify 
        ? decimalToFraction(secondaryValue)
        : decimalToUnsimplifiedFraction(secondaryValue);
      return formatFractionForMath(fraction, fractionDisplay);
    }
      
    case 'percent':
      const percentValue = secondaryValue * 100;
      return formatNumberWithPrecision(percentValue, locale, 1) + '%';
      
    default:
      return formatNumber(secondaryValue, locale);
  }
}

/**
 * Format a secondary line value using NumberFormatConfig
 * Phase 3: Enhanced formatting with currency and custom units
 * 
 * @param primaryValue - Primary line value
 * @param scaleFactor - Scale factor to apply (as decimal number)
 * @param config - Number format configuration
 * @param locale - User's locale for number formatting
 * @returns Formatted string (LaTeX for fractions, plain text otherwise)
 */
export function formatSecondaryValueWithConfig(
  primaryValue: number,
  scaleFactor: number,
  config: NumberFormatConfig,
  locale: LocaleCode
): string {
  const secondaryValue = primaryValue * scaleFactor;
  return formatNumberWithConfig(secondaryValue, config, locale);
} 